import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Card,
  Button,
  Chip,
  IconButton,
  Skeleton,
  Container,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/SearchRounded';
import RemoveIcon from '@mui/icons-material/RemoveRounded';
import AddIcon from '@mui/icons-material/AddRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import { EmptyState } from '../../components/ui/EmptyState';
import { ErrorRetry } from '../../components/ui/ErrorRetry';
import { searchPharmacyProducts } from '../../api/pharmacyProducts';
import apiClient from '../../lib/axios';
import { useMedicationCart } from '../../contexts/MedicationCartContext';
import { getBranchAvailability } from '../../utils/stockAvailability';
import { CartFab } from '../../components/cart/CartFab';

interface ActivePromotion {
  id: string;
  productName: string;
  description: string;
  backgroundColor: string;
}

function useDebouncedValue<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const Eyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    component="span"
    sx={{
      fontFamily: '"Plus Jakarta Sans", sans-serif',
      fontSize: '0.7rem',
      fontWeight: 700,
      letterSpacing: '0.2em',
      textTransform: 'uppercase',
      color: 'text.secondary',
      lineHeight: 1,
    }}
  >
    {children}
  </Typography>
);

export const Search: React.FC = () => {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query, 300);
  const { items, addItem } = useMedicationCart();
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const qty = (id: string) => quantities[id] ?? 1;
  const setQty = (id: string, max: number, n: number) => {
    setQuantities((prev) => ({ ...prev, [id]: Math.max(1, Math.min(max, Math.floor(n))) }));
  };

  const shouldQuery = debouncedQuery.trim().length >= 2;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['pharmacy-products-search', debouncedQuery],
    queryFn: () => searchPharmacyProducts(debouncedQuery),
    enabled: shouldQuery,
  });

  const inCart = (id: string) => items.some((i) => i.pharmacyProductId === id);

  // Promotions carousel (advertisement) — shown below the search box while
  // no query is active. Same data source as the doctor dashboard hero.
  const { data: promotions = [], isLoading: isPromosLoading } = useQuery<ActivePromotion[]>({
    queryKey: ['active-promotions'],
    queryFn: async () => {
      const { data } = await apiClient.get<ActivePromotion[]>('/promotions/active');
      return data;
    },
  });

  const [promoIndex, setPromoIndex] = useState(0);
  useEffect(() => {
    if (promotions.length <= 1) return;
    const timer = setInterval(() => {
      setPromoIndex((prev) => (prev + 1) % promotions.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [promotions.length]);
  const activePromo = promotions.length > 0 ? promotions[promoIndex % promotions.length] : null;

  return (
    <>
      <Container
        maxWidth="md"
        sx={{
          mt: { xs: 2, sm: 5 },
          pb: { xs: 14, sm: 8 },
          px: { xs: 2, sm: 3 },
        }}
      >
        {/* Hero */}
        <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 }, mt: { xs: 1, sm: 2 } }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: '"Bricolage Grotesque", "Plus Jakarta Sans", sans-serif',
              fontWeight: 500,
              fontSize: { xs: '2rem', sm: '2.6rem' },
              lineHeight: 1.05,
              letterSpacing: '-0.025em',
              color: 'text.headline',
              mb: 1,
            }}
          >
            Escribe el nombre del producto
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Bricolage Grotesque", "Plus Jakarta Sans", sans-serif',
              fontSize: { xs: '0.95rem', sm: '1.05rem' },
              fontStyle: 'italic',
              color: 'text.secondary',
              maxWidth: 480,
              mx: 'auto',
            }}
          >
            Tu farmacia, a un click
          </Typography>
        </Box>

        {/* Hero search input */}
        <Box sx={{ position: 'relative', mb: { xs: 3, sm: 4 } }}>
          <TextField
            fullWidth
            label="Buscar"
            placeholder="Paracetamol, aspirina, ibuprofeno…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputLabelProps={{ sx: { color: 'text.secondary' } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                </InputAdornment>
              ),
              sx: {
                bgcolor: 'background.paper',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.headline',
                borderRadius: 999,
                minHeight: 56,
                boxShadow: '0 1px 0 rgba(13,27,53,0.04), 0 8px 24px -12px rgba(13,27,53,0.18)',
                transition: 'box-shadow 180ms ease-out',
                '&.Mui-focused': {
                  boxShadow: '0 0 0 4px rgba(0,102,204,0.08), 0 8px 24px -12px rgba(13,27,53,0.22)',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'divider',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.light',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                  borderWidth: '1.5px',
                },
              },
            }}
          />
        </Box>

        {!shouldQuery && (
          <Box>
            {/* Brand mark */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  bgcolor: 'primary.softBg',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  component="img"
                  src="/logo-icon.svg"
                  alt="Lysi"
                  sx={{ width: 40, height: 40, objectFit: 'contain' }}
                />
              </Box>
            </Box>

            {/* Promotions carousel (advertisement) */}
            {isPromosLoading ? (
              <Skeleton variant="rounded" sx={{ borderRadius: 3, height: 150 }} />
            ) : activePromo ? (
              <Box
                role="button"
                tabIndex={0}
                onClick={() => setQuery(activePromo.productName)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setQuery(activePromo.productName);
                }}
                sx={{
                  borderRadius: 3,
                  bgcolor: activePromo.backgroundColor,
                  backgroundImage: 'radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)',
                  backgroundSize: '18px 18px',
                  p: { xs: '20px 22px', sm: '24px 28px' },
                  minHeight: 150,
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  transition: 'background-color 0.9s ease-in-out, transform 140ms ease-out, box-shadow 140ms ease-out',
                  boxShadow: '0 8px 24px -14px rgba(13,27,53,0.4)',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 12px 28px -14px rgba(13,27,53,0.5)',
                  },
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: -50,
                    right: -50,
                    width: 180,
                    height: 180,
                    borderRadius: '50%',
                    border: '1px solid rgba(255,255,255,0.08)',
                    pointerEvents: 'none',
                  }}
                />
                <Box
                  component="span"
                  sx={{
                    alignSelf: 'flex-start',
                    display: 'inline-block',
                    background: '#E53935',
                    color: '#fff',
                    borderRadius: '4px',
                    fontSize: 9,
                    fontWeight: 700,
                    px: 1.25,
                    py: 0.375,
                    letterSpacing: 1.5,
                    textTransform: 'uppercase',
                    mb: 1.5,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                  }}
                >
                  Nuevo
                </Box>
                <Typography
                  sx={{
                    fontFamily: '"Bricolage Grotesque", "Plus Jakarta Sans", sans-serif',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: { xs: '1.3rem', sm: '1.6rem' },
                    lineHeight: 1.15,
                    letterSpacing: '-0.02em',
                    mb: 1,
                  }}
                >
                  {activePromo.productName}
                </Typography>
                <Typography
                  sx={{
                    color: 'rgba(255,255,255,0.7)',
                    fontSize: 13,
                    lineHeight: 1.5,
                    maxWidth: 420,
                  }}
                >
                  {activePromo.description}
                </Typography>
                {promotions.length > 1 && (
                  <Box sx={{ display: 'flex', gap: 0.75, mt: 2 }}>
                    {promotions.map((_, i) => (
                      <Box
                        key={i}
                        sx={{
                          width: i === promoIndex ? 16 : 6,
                          height: 6,
                          borderRadius: 3,
                          bgcolor: i === promoIndex ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.3)',
                          transition: 'all 0.3s ease',
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            ) : null}
          </Box>
        )}

        {shouldQuery && isLoading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {[0, 1, 2].map((i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={132}
                sx={{ borderRadius: 2, opacity: 1 - i * 0.18 }}
              />
            ))}
          </Box>
        )}

        {shouldQuery && isError && (
          <ErrorRetry message="Error al cargar resultados." onRetry={() => refetch()} />
        )}

        {shouldQuery && data && data.results.length === 0 && (
          <EmptyState title={`No encontramos productos para «${debouncedQuery}».`} />
        )}

        {shouldQuery && data && data.results.length > 0 && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5, px: 0.5 }}>
              <Eyebrow>
                {data.results.length} {data.results.length === 1 ? 'resultado' : 'resultados'}
              </Eyebrow>
              <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {data.results.map((r, idx) => {
                const isInCart = inCart(r.id);
                const outOfStock = r.totalQuantity === 0;
                const cap = Math.min(99, r.totalQuantity);
                return (
                  <Card
                    key={r.id}
                    sx={{
                      p: 0,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: r.isRecommended ? '#F2D9A3' : 'divider',
                      boxShadow: r.isRecommended
                        ? '0 1px 0 rgba(180,131,0,0.06), 0 6px 18px -10px rgba(180,131,0,0.18)'
                        : '0 1px 0 rgba(13,27,53,0.04)',
                      animation: 'customerStaggerIn 240ms ease-out both',
                      animationDelay: `${Math.min(idx, 12) * 40}ms`,
                      transition: 'transform 140ms ease-out, box-shadow 140ms ease-out',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 6px 18px rgba(13,27,53,0.08)',
                      },
                    }}
                  >
                    {/* Top: name + price */}
                    <Box sx={{ p: 2, pb: 1.5 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {r.isRecommended && (
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                px: 0.875,
                                py: 0.25,
                                mb: 0.75,
                                borderRadius: 999,
                                bgcolor: '#FFF7EB',
                                border: '1px solid',
                                borderColor: '#F2D9A3',
                              }}
                            >
                              <StarRoundedIcon sx={{ fontSize: 13, color: '#B45309' }} />
                              <Typography
                                component="span"
                                sx={{
                                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                                  fontSize: '0.66rem',
                                  fontWeight: 700,
                                  letterSpacing: '0.08em',
                                  textTransform: 'uppercase',
                                  color: '#8A4B07',
                                  lineHeight: 1,
                                }}
                              >
                                Recomendado
                              </Typography>
                            </Box>
                          )}
                          <Typography
                            sx={{
                              fontFamily: '"Bricolage Grotesque", "Plus Jakarta Sans", sans-serif',
                              fontWeight: 500,
                              fontSize: { xs: '1.05rem', sm: '1.15rem' },
                              lineHeight: 1.2,
                              letterSpacing: '-0.015em',
                              color: 'text.headline',
                            }}
                          >
                            {r.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              display: 'block',
                              mt: 0.5,
                              fontVariant: 'tabular-nums',
                            }}
                          >
                            {r.chainName}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                            {r.branches.map((branch) => {
                              const { label, bg, color } = getBranchAvailability(branch.name, branch.quantity);
                              return (
                                <Chip
                                  key={branch.name}
                                  label={label}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    background: bg,
                                    color,
                                    borderRadius: '6px',
                                    fontSize: 10,
                                    fontWeight: 600,
                                    '& .MuiChip-label': { px: 0.75 },
                                  }}
                                />
                              );
                            })}
                          </Box>
                        </Box>
                      </Box>
                      {r.isControlled && (
                        <Box
                          sx={{
                            mt: 1.25,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: '#FFF7EB',
                            border: '1px solid',
                            borderColor: '#FFE4BD',
                          }}
                        >
                          <WarningRoundedIcon sx={{ fontSize: 14, color: '#E65100' }} />
                          <Typography
                            sx={{
                              fontFamily: '"Plus Jakarta Sans", sans-serif',
                              fontSize: '0.72rem',
                              fontWeight: 600,
                              letterSpacing: '0.02em',
                              color: '#9A3F00',
                            }}
                          >
                            Receta médica requerida
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Bottom: stepper + add button */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1.5,
                        px: 2,
                        py: 1.25,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'rgba(244, 247, 252, 0.6)',
                      }}
                    >
                      {!isInCart ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0,
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 999,
                            px: 0.25,
                          }}
                        >
                          <IconButton
                            size="small"
                            aria-label={`Reducir para ${r.id}`}
                            disabled={qty(r.id) <= 1 || outOfStock}
                            onClick={() => setQty(r.id, cap, qty(r.id) - 1)}
                            sx={{ color: 'text.headline' }}
                          >
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                          <Box
                            aria-label={`Cantidad para ${r.id}`}
                            sx={{
                              minWidth: 28,
                              textAlign: 'center',
                              fontWeight: 600,
                              fontVariant: 'tabular-nums',
                              color: 'text.headline',
                              fontFamily: '"Plus Jakarta Sans", sans-serif',
                            }}
                          >
                            {qty(r.id)}
                          </Box>
                          <IconButton
                            size="small"
                            title={`Aumentar cantidad para ${r.id}`}
                            disabled={qty(r.id) >= cap || outOfStock}
                            onClick={() => setQty(r.id, cap, qty(r.id) + 1)}
                            sx={{ color: 'text.headline' }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.secondary', fontStyle: 'italic' }}
                        >
                          Ya en tu carrito
                        </Typography>
                      )}

                      <Button
                        variant={isInCart ? 'outlined' : 'contained'}
                        size="small"
                        disabled={isInCart || outOfStock}
                        startIcon={
                          isInCart ? (
                            <CheckRoundedIcon sx={{ fontSize: 18 }} />
                          ) : undefined
                        }
                        onClick={() =>
                          addItem({
                            pharmacyProductId: r.id,
                            name: r.name,
                            chainName: r.chainName,
                            price: r.price,
                            quantity: qty(r.id),
                          })
                        }
                        sx={{
                          minHeight: 40,
                          px: 2,
                          borderRadius: 999,
                          fontWeight: 600,
                          letterSpacing: '0.01em',
                          ...(isInCart && {
                            color: 'success.dark',
                            borderColor: 'success.light',
                            bgcolor: 'transparent',
                            '&.Mui-disabled': {
                              color: 'success.dark',
                              borderColor: 'success.light',
                            },
                          }),
                          ...(!isInCart && {
                            boxShadow: '0 4px 12px -4px rgba(0,102,204,0.45)',
                            '&:hover': {
                              boxShadow: '0 6px 16px -4px rgba(0,102,204,0.55)',
                            },
                          }),
                        }}
                      >
                        {isInCart ? 'En carrito' : '+ Carrito'}
                      </Button>
                    </Box>
                  </Card>
                );
              })}
            </Box>
          </Box>
        )}
      </Container>
      <CartFab />
    </>
  );
};
